import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import loansService, { Loan, UpdateLoanStatusRequest } from '../../services/loansService';
import LoanStatusDialog from './LoanStatusDialog';

interface LoanDetailsProps {
  open: boolean;
  onClose: () => void;
  loanId: string | null;
  onEdit?: (loan: Loan) => void;
}

const LoanDetails: React.FC<LoanDetailsProps> = ({
  open,
  onClose,
  loanId,
  onEdit
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: loan, isLoading, error } = useQuery({
    queryKey: ['loan', loanId],
    queryFn: () => loanId ? loansService.getLoan(loanId) : null,
    enabled: !!loanId && open
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, statusUpdate }: { id: string; statusUpdate: UpdateLoanStatusRequest }) =>
      loansService.updateLoanStatus(id, statusUpdate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['loan', loanId] });
      setStatusDialogOpen(false);
    }
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleStatusUpdate = (statusUpdate: UpdateLoanStatusRequest) => {
    if (loan) {
      updateStatusMutation.mutate({ id: loan.id, statusUpdate });
    }
  };

  const getProgressValue = (status: string): number => {
    const statusProgress: Record<string, number> = {
      'Application': 10,
      'Under Review': 25,
      'Additional Info Required': 40,
      'Approved': 60,
      'Funded': 80,
      'Current': 100,
      'Paid Off': 100,
      'Denied': 0,
      'Charged Off': 0,
      'Default': 50,
      'Past Due': 90
    };
    return statusProgress[status] || 0;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="error">Failed to load loan details</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">
                {loan ? `Loan ${loan.loanNumber}` : 'Loan Details'}
              </Typography>
              {loan && (
                <Typography variant="body2" color="text.secondary">
                  {loan.client?.businessName || 'Unknown Client'}
                </Typography>
              )}
            </Box>
            {loan && (
              <IconButton onClick={handleMenuOpen}>
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography>Loading loan details...</Typography>
            </Box>
          ) : loan ? (
            <Grid container spacing={3}>
              {/* Status and Progress */}
              <Grid size={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Loan Status</Typography>
                      <Chip
                        label={loan.status}
                        color={loansService.getStatusColor(loan.status)}
                        size="medium"
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getProgressValue(loan.status)}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Loan Progress: {getProgressValue(loan.status)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Basic Information */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Loan Information</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.secondary">Loan Number</Typography>
                        <Typography variant="body1" fontWeight="medium">{loan.loanNumber}</Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.secondary">Type</Typography>
                        <Typography variant="body1">{loan.type}</Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.secondary">Purpose</Typography>
                        <Typography variant="body1">{loan.purpose}</Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.secondary">Term</Typography>
                        <Typography variant="body1">{loan.termMonths} months</Typography>
                      </Grid>
                      <Grid size={12}>
                        <Typography variant="body2" color="text.secondary">Application Date</Typography>
                        <Typography variant="body1">{formatDate(loan.applicationDate)}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Financial Details */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MoneyIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Financial Details</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.secondary">Requested Amount</Typography>
                        <Typography variant="h6" color="primary.main">
                          {loansService.formatCurrency(loan.requestedAmount)}
                        </Typography>
                      </Grid>
                      {loan.approvedAmount && (
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Approved Amount</Typography>
                          <Typography variant="h6" color="success.main">
                            {loansService.formatCurrency(loan.approvedAmount)}
                          </Typography>
                        </Grid>
                      )}
                      {loan.interestRate && (
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Interest Rate</Typography>
                          <Typography variant="body1">{loan.interestRate}% APR</Typography>
                        </Grid>
                      )}
                      {loan.paymentFrequency && (
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Payment Frequency</Typography>
                          <Typography variant="body1">{loan.paymentFrequency}</Typography>
                        </Grid>
                      )}
                      {loan.paymentAmount && (
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Payment Amount</Typography>
                          <Typography variant="body1">
                            {loansService.formatCurrency(loan.paymentAmount)}
                          </Typography>
                        </Grid>
                      )}
                      {loan.outstandingBalance && (
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Outstanding Balance</Typography>
                          <Typography variant="body1">
                            {loansService.formatCurrency(loan.outstandingBalance)}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Key Dates */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Important Dates</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.secondary">Application</Typography>
                        <Typography variant="body1">{formatDate(loan.applicationDate)}</Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.secondary">Approval</Typography>
                        <Typography variant="body1">{formatDate(loan.approvalDate)}</Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.secondary">Funding</Typography>
                        <Typography variant="body1">{formatDate(loan.fundingDate)}</Typography>
                      </Grid>
                      <Grid size={6}>
                        <Typography variant="body2" color="text.secondary">Maturity</Typography>
                        <Typography variant="body1">{formatDate(loan.maturityDate)}</Typography>
                      </Grid>
                      {loan.nextPaymentDate && (
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Next Payment</Typography>
                          <Typography variant="body1">{formatDate(loan.nextPaymentDate)}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Collateral */}
              {loan.collateral && loan.collateral.type !== 'None' && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6">Collateral</Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2}>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Type</Typography>
                          <Typography variant="body1">{loan.collateral.type}</Typography>
                        </Grid>
                        {loan.collateral.value && (
                          <Grid size={6}>
                            <Typography variant="body2" color="text.secondary">Value</Typography>
                            <Typography variant="body1">
                              {loansService.formatCurrency(loan.collateral.value)}
                            </Typography>
                          </Grid>
                        )}
                        {loan.collateral.description && (
                          <Grid size={12}>
                            <Typography variant="body2" color="text.secondary">Description</Typography>
                            <Typography variant="body1">{loan.collateral.description}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Client Information */}
              {loan.client && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Client Information</Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <Typography variant="body2" color="text.secondary">Business Name</Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {loan.client.businessName}
                          </Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Contact</Typography>
                          <Typography variant="body1">
                            {loan.client.contactInfo.primaryContactName}
                          </Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="body2" color="text.secondary">Email</Typography>
                          <Typography variant="body1">
                            {loan.client.contactInfo.email}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Underwriting Notes */}
              {loan.underwritingNotes && (
                <Grid size={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Underwriting Notes</Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {loan.underwritingNotes}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          ) : (
            <Alert severity="warning">Loan not found</Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          {loan && onEdit && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                onEdit(loan);
                onClose();
              }}
            >
              Edit Loan
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            setStatusDialogOpen(true);
            handleMenuClose();
          }}
        >
          Update Status
        </MenuItem>
        {loan && onEdit && (
          <MenuItem
            onClick={() => {
              onEdit(loan);
              handleMenuClose();
              onClose();
            }}
          >
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit Loan
          </MenuItem>
        )}
      </Menu>

      {/* Status Update Dialog */}
      {loan && (
        <LoanStatusDialog
          open={statusDialogOpen}
          onClose={() => setStatusDialogOpen(false)}
          loan={loan}
          onUpdate={handleStatusUpdate}
          isLoading={updateStatusMutation.isPending}
        />
      )}
    </>
  );
};

export default LoanDetails;